import React, { useEffect, useMemo, useState } from "react";
import "../../styles/filters-form.less";
import axios from "axios";
import { Button, Col, Form, Input, message, Modal, Row, Select } from "antd";
import {
  fetchDicDataType,
  fetchDicMultiplType,
  fetchDicRequiredType,
  fetchDicStorageType,
  fetchDicViewModeMask,
} from "../../store/slices/rtkDictionaries";
import LocalStorageService from "../../services/LocalStorageService";
import {
  createPropertyTypes,
  fetchPropertyPageSearchTypes,
} from "../../store/slices/property-type-admin";
import dataTypeIdesCodes from "../../lib/const/dataTypeIdesCodes";
import { propertyTypesPageSearchRoute } from "../../lib/routes/property-type-admin";
import { checkIsLoaded } from "../../lib/utils/checkLoadingStatus";
import { useAppDispatch, useAppSelector } from "../..";
import { ObjectType, PropertyType, PropertyTypeSearchParams } from "../../lib/interfaces";
import { getObjectTypesRoute } from "../../lib/routes/dictionaries";
import { DataTypeCode } from "../../lib/types";
import { fetchDictionaryTypes } from "../../store/slices/dicts";
import RequiredMark from "../common/RequiredMark/RequiredMark";

interface ComponentProps {
  setFormCreate: React.Dispatch<React.SetStateAction<boolean>>;
  propertyTypeSearchParams: PropertyTypeSearchParams;
}

const PropertyTypesAdminCreate = ({ setFormCreate, propertyTypeSearchParams }: ComponentProps) => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm<PropertyType>();
  const { Option } = Select;
  const [dataTypeId, setDataTypeId] = useState<DataTypeCode>();

  const {
    dicDataTypes,
    dicDataTypesLoading,
    dicRequiredType,
    dicRequiredTypeLoading,
    dicMultiplType,
    dicStorageTypes,
    dicStorageTypesLoading,
    dicViewModeMasks,
    dicViewModeMasksLoading,
  } = useAppSelector((state) => state.rtkDictionaries);

  const { dictionaryTypes, dictionaryTypesLoading } = useAppSelector((state) => state.dicts);
  const { tenant } = useAppSelector((state) => state.tenants);

  const handleChangeValue = (value: DataTypeCode) => {
    setDataTypeId((prevState) => {
      if (prevState !== value) {
        const resetFieldNames: (keyof PropertyType)[] = [
          "multiplTypeId",
          "requiredTypeId",
          "specificDicTypeAlias",
          "valDicTypeAlias",
        ];
        form.resetFields(resetFieldNames);
      }
      return value;
    });
  };

  useEffect(() => {
    dispatch(fetchDicDataType());
    dispatch(fetchDicRequiredType());
    dispatch(fetchDicMultiplType());
    dispatch(fetchDictionaryTypes());
    dispatch(fetchDicStorageType());
    dispatch(fetchDicViewModeMask());
  }, []);

  const multiplyData = useMemo(
    () =>
      dicMultiplType.filter(({ alias }) => {
        const isDictionary = alias.includes("dictionary");
        return dataTypeId === dataTypeIdesCodes.dictionary ? isDictionary : !isDictionary;
      }),
    [dicMultiplType, dataTypeId]
  );

  const validateMessages = {
    required: "",
  };

  const onFinish = async (values: PropertyType) => {
    if (values) {
      values.propertyForDic = false;
      values.tenantId = Number(LocalStorageService.getTenantId());
    }

    const obj = {
      page: 0,
      size: 50,
      propertyTypeAlias: values?.alias,
    };

    const { data } = await axios(propertyTypesPageSearchRoute(obj));
    const content = data?.content;

    const findObjectTypeSysname = content?.find(
      (item) => item.objectTypeSysname === values.objectTypeSysname
    );

    if (!findObjectTypeSysname) {
      const { data: objectType } = await axios.get<ObjectType>(
        getObjectTypesRoute(values.objectTypeSysname)
      );

      if (objectType) {
        dispatch(
          createPropertyTypes({
            objectTypeSysname: values.objectTypeSysname,
            dataPost: { ...values, objectTypeId: objectType.id },
          })
        )
          .then(() => {
            dispatch(fetchPropertyPageSearchTypes(propertyTypeSearchParams));
            setFormCreate(false);
          })
          .catch((e) => {
            Modal.error({
              title: "Ошибка создания типа свойства. Обратитесь к администратору",
              content: e.message,
            });
          });
      } else {
        Modal.error({
          title: `Ошибка создания. Объекта "${values.objectTypeSysname}" не существует`,
        });
      }
    } else {
      message.warning("Свойство с таким Alias уже существует");
    }
  };

  return (
    <div className="container_height">
      <div className="inner-row">
        <h1>Создание нового типа свойства</h1>
        <Row>
          <Form
            style={{ width: "100%" }}
            form={form}
            validateMessages={validateMessages}
            name="advanced_search"
            className="create-form"
            onFinish={onFinish}
          >
            <Row className="create-form__row-label" align="middle" gutter={[16, 16]}>
              <Col span={2}>
                <RequiredMark>
                  <label>Объект</label>
                </RequiredMark>
              </Col>
              <Col span={1}>
                <RequiredMark>
                  <label>Alias</label>
                </RequiredMark>
              </Col>
              <Col span={2}>
                <label>Группа свойств</label>
              </Col>
              <Col span={2}>
                <RequiredMark>
                  <label>Наименование</label>
                </RequiredMark>
              </Col>
              <Col span={2}>
                <label>Комментарий</label>
              </Col>
              <Col span={1}>
                <label>Тенант</label>
              </Col>
              <Col span={2}>
                <RequiredMark>
                  <label>Тип данных</label>
                </RequiredMark>
              </Col>
              <Col span={2}>
                <RequiredMark>
                  <label>Обязательность</label>
                </RequiredMark>
              </Col>
              <Col span={2}>
                <label>Множественность значений</label>
              </Col>
              <Col span={2}>
                <label>Словарь (параметр)</label>
              </Col>
              <Col span={2}>
                <label>Словарь (значение)</label>
              </Col>
              <Col span={2}>
                <RequiredMark>
                  <label>Способ хранения property</label>
                </RequiredMark>
              </Col>
              <Col span={2}>
                <RequiredMark>
                  <label>Тип отображения</label>
                </RequiredMark>
              </Col>
            </Row>

            <Row
              className="create-form__row-content"
              style={{ marginTop: "20px", marginBottom: "100px" }}
              align="top"
              gutter={[16, 16]}
            >
              <Col span={2}>
                <Form.Item
                  className="create-form__col form-item-required__hidden-error__mb0"
                  name="objectTypeSysname"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>

              <Col span={1}>
                <Form.Item
                  className="create-form__col form-item-required__hidden-error__mb0"
                  name="alias"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>

              <Col span={2}>
                <Form.Item
                  className="create-form__col form-item-required__hidden-error__mb0"
                  name="group"
                >
                  <Input />
                </Form.Item>
              </Col>

              <Col span={2}>
                <Form.Item
                  className="create-form__col form-item-required__hidden-error__mb0"
                  name="name"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>

              <Col span={2}>
                <Form.Item
                  className="create-form__col form-item-required__hidden-error__mb0"
                  name="dsc"
                >
                  <Input />
                </Form.Item>
              </Col>

              <Col span={1}>{tenant?.name}</Col>

              <Col span={2}>
                <Form.Item
                  className="create-form__col form-item-required__hidden-error__mb0"
                  name="dataType"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Select
                    placeholder="Выберите"
                    onChange={(value: DataTypeCode) => handleChangeValue(value)}
                    style={{ minWidth: "100px" }}
                  >
                    {checkIsLoaded(dicDataTypesLoading) &&
                      dicDataTypes.map((item) => (
                        <Option key={item.id} value={item.id}>
                          {item.name}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col span={2}>
                <Form.Item
                  className="create-form__col form-item-required__hidden-error__mb0"
                  name="requiredTypeId"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Select placeholder="Выберите" style={{ minWidth: "100px" }} showArrow>
                    {checkIsLoaded(dicRequiredTypeLoading) &&
                      dicRequiredType.map((item) => (
                        <Option key={item.id} value={item.id}>
                          {item.name}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col span={2}>
                <Form.Item
                  className="create-form__col form-item-required__hidden-error__mb0"
                  name="multiplTypeId"
                  rules={[
                    {
                      required: !!dataTypeId,
                    },
                  ]}
                >
                  <Select
                    placeholder="Выберите"
                    disabled={!dataTypeId}
                    style={{ minWidth: "100px" }}
                  >
                    {multiplyData.map((item) => (
                      <Option key={item.id} value={item.id}>
                        {item.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col span={2}>
                <Form.Item
                  className="create-form__col form-item-required__hidden-error__mb0"
                  name="specificDicTypeAlias"
                >
                  <Select
                    placeholder="Выберите"
                    allowClear={dataTypeId !== dataTypeIdesCodes.dictionary}
                    disabled={!dataTypeId}
                    style={{ minWidth: "100px" }}
                  >
                    {checkIsLoaded(dicDataTypesLoading) &&
                      dictionaryTypes.map((item) => (
                        <Option key={item.id} value={item.alias}>
                          {item.name}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col span={2}>
                <Form.Item
                  className="create-form__col form-item-required__hidden-error__mb0"
                  name="valDicTypeAlias"
                  rules={[
                    {
                      required: dataTypeId === dataTypeIdesCodes.dictionary,
                    },
                  ]}
                >
                  <Select
                    placeholder="Выберите"
                    disabled={dataTypeId !== dataTypeIdesCodes.dictionary}
                    style={{ minWidth: "100px" }}
                  >
                    {checkIsLoaded(dictionaryTypesLoading) &&
                      dictionaryTypes.map((item) => (
                        <Option key={item.id} value={item.alias}>
                          {item.name}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col span={2}>
                <Form.Item
                  className="create-form__col form-item-required__hidden-error__mb0"
                  name="storageTypeId"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Select placeholder="Выберите" style={{ minWidth: "100px" }}>
                    {checkIsLoaded(dicStorageTypesLoading) &&
                      dicStorageTypes.map((item) => (
                        <Option key={item.id} value={item.id}>
                          {item.name}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col span={2}>
                <Form.Item
                  className="create-form__col form-item-required__hidden-error__mb0"
                  name="viewModeMask"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Select placeholder="Выберите" style={{ minWidth: "100px" }}>
                    {checkIsLoaded(dicViewModeMasksLoading) &&
                      dicViewModeMasks.map((item) => (
                        <Option key={item.id} value={item.id}>
                          {item.name}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row
              align="middle"
              style={{ borderTop: "1px solid #CCDDEE", paddingTop: "10px" }}
              justify="center"
            >
              <Col
                span={24}
                style={{
                  textAlign: "center",
                }}
              >
                <Button
                  style={{
                    margin: "0 8px",
                  }}
                  onClick={() => {
                    form.resetFields();
                    setFormCreate(false);
                  }}
                >
                  Отмена
                </Button>
                <Button type="primary" htmlType="submit">
                  Сохранить
                </Button>
              </Col>
            </Row>
          </Form>
        </Row>
      </div>
    </div>
  );
};

export default PropertyTypesAdminCreate;
